import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Property } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin } from "lucide-react";
import { DARK_MAP_STYLES, useIsDarkClass } from "@/lib/map-styles";
import { resolveBrandColor } from "@/lib/brand-colors";
import {
  isPointServiceable,
  normalizePolygonOrder,
  type ServiceAreaRoute,
} from "@shared/trash-geo";

export { isPointServiceable, normalizePolygonOrder };

/** A service-area route polygon as returned by GET /api/app/trash/routes. */
export type TrashRoute = ServiceAreaRoute;

const IN_AREA_MARKER = "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
const OUT_AREA_MARKER = "https://maps.google.com/mapfiles/ms/icons/grey.png";

/** A property is eligible for scheduled / on-demand service only when active or launch. */
function isServiceEligibleStatus(status: string | null | undefined): boolean {
  return status === "active" || status === "launch";
}

/** The current trash enrollment for a mapped property, as far as the map cares. */
export interface MapEnrollment {
  id: number;
  /** Enrollment lifecycle status (`active` / `paused` / `pending` / …). */
  status: string;
}

/**
 * True when an enrollment means the property is already signed up (so the map
 * should not offer a misleading "Enroll" button). `cancelled` enrollments are
 * treated as not-enrolled so the host can re-enroll from the marker.
 */
export function isEnrolledStatus(status: string | null | undefined): boolean {
  return status === "active" || status === "paused" || status === "pending";
}

interface TrashCoverageMapProps {
  properties: Property[];
  /**
   * Map of `propertyId` → its current enrollment. A property absent from this
   * map (or with a `cancelled` enrollment) is treated as not enrolled.
   */
  enrollmentByPropertyId?: Record<number, MapEnrollment>;
  /** Open the enrollment flow pre-filled for this property. */
  onEnroll?: (property: Property) => void;
  /** Open the on-demand pickup flow pre-filled for this property. */
  onRequestPickup?: (property: Property) => void;
  /** Pause an active enrollment for this property. */
  onPause?: (property: Property, enrollment: MapEnrollment) => void;
  /** Resume a paused enrollment for this property. */
  onResume?: (property: Property, enrollment: MapEnrollment) => void;
}

export default function TrashCoverageMap({
  properties,
  enrollmentByPropertyId,
  onEnroll,
  onRequestPickup,
  onPause,
  onResume,
}: TrashCoverageMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const polygonsRef = useRef<google.maps.Polygon[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // Keep the latest action callbacks in refs so the (DOM-built) info-window
  // click handlers always call the current props without rebuilding markers.
  const onEnrollRef = useRef(onEnroll);
  const onRequestPickupRef = useRef(onRequestPickup);
  const onPauseRef = useRef(onPause);
  const onResumeRef = useRef(onResume);
  useEffect(() => {
    onEnrollRef.current = onEnroll;
  }, [onEnroll]);
  useEffect(() => {
    onRequestPickupRef.current = onRequestPickup;
  }, [onRequestPickup]);
  useEffect(() => {
    onPauseRef.current = onPause;
  }, [onPause]);
  useEffect(() => {
    onResumeRef.current = onResume;
  }, [onResume]);

  const enrollmentMap = useMemo(
    () => enrollmentByPropertyId ?? {},
    [enrollmentByPropertyId],
  );

  const isDark = useIsDarkClass();

  const { data: mapsKeyData, isLoading: keyLoading } = useQuery<{ key: string }>({
    queryKey: ["/api/app/maps-key"],
  });
  const apiKey = mapsKeyData?.key || "";

  const routesQuery = useQuery<TrashRoute[]>({
    queryKey: ["/api/app/trash/routes"],
    staleTime: 5 * 60_000,
  });
  const routes = useMemo(() => routesQuery.data ?? [], [routesQuery.data]);

  // Properties that have coordinates to plot.
  const mappable = useMemo(
    () =>
      properties
        .map((p) => ({ prop: p, lat: p.latitude, lng: p.longitude }))
        .filter(
          (m): m is { prop: Property; lat: number; lng: number } =>
            m.lat != null && m.lng != null,
        ),
    [properties],
  );

  const loadGoogleMaps = useCallback(() => {
    if (!apiKey) return;
    if (window.google?.maps?.Map) {
      setMapLoaded(true);
      return;
    }
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 100);
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.google?.maps?.Map) {
          clearInterval(check);
          setMapLoaded(true);
        }
      }, 50);
    };
    script.onerror = () => setMapError("Failed to load Google Maps");
    document.head.appendChild(script);
  }, [apiKey]);

  useEffect(() => {
    loadGoogleMaps();
  }, [loadGoogleMaps]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        zoom: 10,
        center: { lat: 33.749, lng: -84.388 },
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        styles: isDark ? DARK_MAP_STYLES : [],
      });
      infoWindowRef.current = new google.maps.InfoWindow();
    }

    const map = mapInstanceRef.current;
    const infoWindow = infoWindowRef.current!;

    // Clear previous polygons + markers before redrawing.
    polygonsRef.current.forEach((p) => p.setMap(null));
    polygonsRef.current = [];
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;
    const brandColor = resolveBrandColor();

    // Draw route polygons.
    routes.forEach((route) => {
      if (!Array.isArray(route.polygon) || route.polygon.length < 3) return;
      const normalized = normalizePolygonOrder(route.polygon);
      const path = normalized.map(([lat, lng]) => ({ lat, lng }));
      const polygon = new google.maps.Polygon({
        paths: path,
        strokeColor: brandColor,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: brandColor,
        fillOpacity: 0.12,
        clickable: false,
        map,
      });
      polygonsRef.current.push(polygon);
      path.forEach((pt) => {
        bounds.extend(pt);
        hasBounds = true;
      });
    });

    // Plot property markers, coloured by serviceability.
    mappable.forEach(({ prop, lat, lng }) => {
      const position = { lat, lng };
      const inArea = isPointServiceable(lat, lng, routes);
      const marker = new google.maps.Marker({
        map,
        position,
        title: prop.name ?? prop.address ?? `Property #${prop.id}`,
        icon: inArea ? IN_AREA_MARKER : OUT_AREA_MARKER,
      });

      marker.addListener("click", () => {
        const dark = document.documentElement.classList.contains("dark");
        const bg = dark ? "#1e1e1e" : "#ffffff";
        const textPrimary = dark ? "#f3f4f6" : "#111827";
        const textSecondary = dark ? "#9ca3af" : "#6b7280";

        const container = document.createElement("div");
        container.style.cssText = `padding:8px;max-width:250px;font-family:system-ui,sans-serif;background:${bg};border-radius:4px;`;

        const nameEl = document.createElement("div");
        nameEl.style.cssText = `font-weight:600;font-size:14px;margin-bottom:4px;color:${textPrimary};`;
        nameEl.textContent = prop.name ?? prop.address ?? `Property #${prop.id}`;
        container.appendChild(nameEl);

        if (prop.address && prop.name) {
          const addrEl = document.createElement("div");
          addrEl.style.cssText = `font-size:12px;color:${textSecondary};margin-bottom:6px;`;
          addrEl.textContent = prop.address;
          container.appendChild(addrEl);
        }

        const statusRow = document.createElement("div");
        statusRow.style.cssText = "display:flex;align-items:center;gap:6px;";
        const dot = document.createElement("span");
        dot.style.cssText = `display:inline-block;width:8px;height:8px;border-radius:50%;background:${inArea ? "#16a34a" : "#9ca3af"};`;
        const statusText = document.createElement("span");
        statusText.style.cssText = `font-size:12px;color:${inArea ? "#16a34a" : textSecondary};font-weight:500;`;
        statusText.textContent = inArea ? "In service area" : "Outside service area";
        statusRow.appendChild(dot);
        statusRow.appendChild(statusText);
        container.appendChild(statusRow);

        // Quick actions: only in-area, service-eligible properties can be
        // enrolled or scheduled for a pickup straight from the map. Everything
        // else gets a short explanation of why no action is offered.
        const noteStyle = `font-size:12px;color:${textSecondary};margin-top:8px;line-height:1.4;`;
        const eligibleStatus = isServiceEligibleStatus(prop.status);

        // Current enrollment for this property (active / paused / pending), if any.
        const enrollment = enrollmentMap[prop.id];
        const enrolled = !!enrollment && isEnrolledStatus(enrollment.status);

        // Helper: a secondary "Request pickup" button. Available to any in-area,
        // eligible property (enrolled or not) when a handler is wired.
        const makeRequestButton = () => {
          const requestBtn = document.createElement("button");
          requestBtn.type = "button";
          requestBtn.textContent = "Request pickup";
          requestBtn.setAttribute("data-testid", `button-map-request-${prop.id}`);
          requestBtn.style.cssText = `flex:1;padding:6px 10px;font-size:12px;font-weight:600;color:${brandColor};background:transparent;border:1px solid ${brandColor};border-radius:6px;cursor:pointer;`;
          requestBtn.addEventListener("click", (ev) => {
            ev.stopPropagation();
            infoWindow.close();
            onRequestPickupRef.current?.(prop);
          });
          return requestBtn;
        };

        if (!inArea) {
          const note = document.createElement("div");
          note.style.cssText = noteStyle;
          note.setAttribute("data-testid", `text-map-out-of-area-${prop.id}`);
          note.textContent =
            "This property is outside the service area, so scheduled collection and on-demand pickups aren't available here.";
          container.appendChild(note);
        } else if (!eligibleStatus) {
          const note = document.createElement("div");
          note.style.cssText = noteStyle;
          note.setAttribute("data-testid", `text-map-status-ineligible-${prop.id}`);
          note.textContent =
            "Set this property to Active or Launch status to enroll it or request a pickup.";
          container.appendChild(note);
        } else {
          // In-area + eligible. Surface the current enrollment status (if any),
          // then offer the action that actually matches that state.

          if (enrolled) {
            const enrollColor =
              enrollment!.status === "active"
                ? "#16a34a"
                : enrollment!.status === "paused"
                ? "#6b7280"
                : "#ca8a04"; // pending
            const enrollLabel =
              enrollment!.status === "active"
                ? "Enrolled · Active"
                : enrollment!.status === "paused"
                ? "Enrolled · Paused"
                : "Enrollment pending approval";
            const enrollRow = document.createElement("div");
            enrollRow.style.cssText = "display:flex;align-items:center;gap:6px;margin-top:6px;";
            const eDot = document.createElement("span");
            eDot.style.cssText = `display:inline-block;width:8px;height:8px;border-radius:50%;background:${enrollColor};`;
            const eText = document.createElement("span");
            eText.style.cssText = `font-size:12px;color:${enrollColor};font-weight:500;`;
            eText.setAttribute("data-testid", `text-map-enrollment-status-${prop.id}`);
            eText.textContent = enrollLabel;
            enrollRow.appendChild(eDot);
            enrollRow.appendChild(eText);
            container.appendChild(enrollRow);
          }

          // Decide which primary action button to render, given the enrollment
          // state and which handlers are wired.
          const canEnroll = !enrolled && !!onEnrollRef.current;
          const canPause = enrolled && enrollment!.status === "active" && !!onPauseRef.current;
          const canResume = enrolled && enrollment!.status === "paused" && !!onResumeRef.current;
          const canRequest = !!onRequestPickupRef.current;

          if (!canEnroll && !canPause && !canResume && !canRequest) {
            // No action handlers wired (e.g. view-only): don't render dead buttons.
            const note = document.createElement("div");
            note.style.cssText = noteStyle;
            note.setAttribute("data-testid", `text-map-readonly-${prop.id}`);
            note.textContent = enrolled
              ? "Already enrolled — manage this enrollment from the Enrollments tab."
              : "In service area — eligible for scheduled collection and on-demand pickup.";
            container.appendChild(note);
          } else {
            const actionsRow = document.createElement("div");
            actionsRow.style.cssText = "display:flex;gap:8px;margin-top:10px;";

            if (canEnroll) {
              const enrollBtn = document.createElement("button");
              enrollBtn.type = "button";
              enrollBtn.textContent = "Enroll";
              enrollBtn.setAttribute("data-testid", `button-map-enroll-${prop.id}`);
              enrollBtn.style.cssText = `flex:1;padding:6px 10px;font-size:12px;font-weight:600;color:#ffffff;background:${brandColor};border:none;border-radius:6px;cursor:pointer;`;
              enrollBtn.addEventListener("click", (ev) => {
                ev.stopPropagation();
                infoWindow.close();
                onEnrollRef.current?.(prop);
              });
              actionsRow.appendChild(enrollBtn);
            }

            if (canPause) {
              const pauseBtn = document.createElement("button");
              pauseBtn.type = "button";
              pauseBtn.textContent = "Pause";
              pauseBtn.setAttribute("data-testid", `button-map-pause-${prop.id}`);
              pauseBtn.style.cssText = `flex:1;padding:6px 10px;font-size:12px;font-weight:600;color:#ffffff;background:${brandColor};border:none;border-radius:6px;cursor:pointer;`;
              pauseBtn.addEventListener("click", (ev) => {
                ev.stopPropagation();
                infoWindow.close();
                onPauseRef.current?.(prop, enrollment!);
              });
              actionsRow.appendChild(pauseBtn);
            }

            if (canResume) {
              const resumeBtn = document.createElement("button");
              resumeBtn.type = "button";
              resumeBtn.textContent = "Resume";
              resumeBtn.setAttribute("data-testid", `button-map-resume-${prop.id}`);
              resumeBtn.style.cssText = `flex:1;padding:6px 10px;font-size:12px;font-weight:600;color:#ffffff;background:${brandColor};border:none;border-radius:6px;cursor:pointer;`;
              resumeBtn.addEventListener("click", (ev) => {
                ev.stopPropagation();
                infoWindow.close();
                onResumeRef.current?.(prop, enrollment!);
              });
              actionsRow.appendChild(resumeBtn);
            }

            if (canRequest) {
              actionsRow.appendChild(makeRequestButton());
            }

            container.appendChild(actionsRow);
          }
        }

        infoWindow.setContent(container);
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
      bounds.extend(position);
      hasBounds = true;
    });

    if (hasBounds) {
      if (mappable.length + polygonsRef.current.length === 1 && mappable.length === 1) {
        map.setCenter({ lat: mappable[0].lat, lng: mappable[0].lng });
        map.setZoom(13);
      } else {
        map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });
      }
    }
  }, [mapLoaded, routes, mappable, isDark]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setOptions({ styles: isDark ? DARK_MAP_STYLES : [] });
  }, [isDark]);

  if (keyLoading || routesQuery.isLoading) {
    return <Skeleton className="h-[500px] w-full rounded-lg" data-testid="trash-coverage-map-loading" />;
  }

  if (!apiKey) {
    return (
      <div className="border rounded-lg p-6 text-center text-gray-500" data-testid="trash-coverage-map-no-key">
        <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">Map view is unavailable.</p>
      </div>
    );
  }

  if (mapError || routesQuery.isError) {
    return (
      <div className="border rounded-lg p-6 text-center text-gray-500" data-testid="trash-coverage-map-error">
        <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">{mapError ?? "Could not load the coverage map. Please try again."}</p>
      </div>
    );
  }

  if (!mapLoaded) {
    return <Skeleton className="h-[500px] w-full rounded-lg" data-testid="trash-coverage-map-loading" />;
  }

  return (
    <div data-testid="trash-coverage-map-container">
      <div
        ref={mapRef}
        className="w-full h-[500px] rounded-lg border shadow-sm"
        data-testid="trash-coverage-map"
      />
      <div className="flex flex-wrap items-center gap-4 mt-3 px-1 text-xs text-muted-foreground" data-testid="trash-coverage-legend">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: "#16a34a" }} />
          In service area
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: "#9ca3af" }} />
          Outside service area
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-4 h-3 rounded-sm border border-brand bg-brand/10" />
          Coverage zone
        </span>
      </div>
      {routes.length === 0 && (
        <p className="text-sm text-gray-500 mt-2" data-testid="text-no-routes">
          No service-area coverage zones are available to display yet.
        </p>
      )}
      {routes.length > 0 && mappable.length === 0 && properties.length > 0 && (
        <p className="text-sm text-gray-500 mt-2" data-testid="text-no-mappable-properties">
          None of your properties have location data to plot. Re-save a property on its detail page to geocode it.
        </p>
      )}
    </div>
  );
}
